import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectIsSupportChatTestFeatureFlagOn, selectNBAJourneysEnabled } from 'core/userContext';
import { AllMessageMedium, CampaignType } from 'modules/domains/campaign/types';
import { MessageMedium } from 'modules/graphql/types';
import { TemplateType } from 'constants/templateTypes';
import {
  LastSavedIndicator,
  PreferencesButton,
  SaveSavedRowDesignComboButton,
  SmsSmartOptInButton,
  SnippetButton,
} from 'modules/templateEditor/common/Controls';
import { EditSavedRowDesignWarning } from 'modules/templateEditor/common/EditSavedRowDesignWarning';
import {
  TemplateEditViews,
  UnselectedMessageMedium,
  UpdateMediumFieldsType,
} from 'modules/templateEditor/common/types';
import { useCampaignQueryContext } from 'modules/templateEditor/context/CampaignQueryContext';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useSendTestMsgContext } from 'modules/templateEditor/context/SendTestMsgContext';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { CommonModals, SendTestMsgModals } from 'modules/templateEditor/modals';
import { Badge } from 'ui/badge';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS, ChatButton } from 'ui/button';
import { ArrowBack, Exit24 } from 'ui/icons';
import { Spinner } from 'ui/spinner';
import { Takeover } from 'ui/workflows/takeover';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import noop, { NoOp } from 'utils/noop';

import { useEditSavedRowDesignContext } from 'modules/templateEditor/context/EditSavedRowDesignContext';
import { useOnRequestCloseSavedRowDesign } from 'modules/templateEditor/hooks/useOnRequestCloseSavedRowDesign';
import { BADGE_VARIANTS_FOR_CAMPAIGN_STATUSES } from '../../../campaignIndex/constants';
import AttachTemplateTakeover from './AttachTemplateTakeover';
import { NBASuggestion } from './common/components/NBASuggestion';
import { SaveButtonCombo } from './common/components/SaveButtonCombo';
import { TemplateDetailsSubHeader } from './common/components/TemplateDetailsSubHeader';
import { InStudioView } from './common/enums';
import { InStudioUnsavedChangesPrompts } from './common/modals/InStudioUnsavedChangesPrompts';
import { useInStudioTakeoverContext } from './context';
import { useEditorContext, useEditorViewLoader } from './hooks';
import { InStudioEditToolbar } from './InStudioEditToolbar';
import { CampaignTemplateProvider, InStudioTemplateContext } from './InStudioTemplateEditContext';
import { ConsumingApp, InStudioTemplateProps } from './InStudioTemplateEditing.types';
import {
  DefaultFooterWrapper,
  EditorFooterWrapper,
  StyledButtonGroup,
  Wrapper,
} from './InStudioTemplateEditor.styled';

type TakeOverFooterProps = {
  onClose: () => void;
};

const EditorFooter = () => {
  const { isEditingSavedRowDesign } = useEditSavedRowDesignContext();

  return (
    <EditorFooterWrapper>
      <StyledButtonGroup>
        <SnippetButton />
        <PreferencesButton />
        <SmsSmartOptInButton />
      </StyledButtonGroup>
      <StyledButtonGroup>
        <LastSavedIndicator />
        {isEditingSavedRowDesign ? <SaveSavedRowDesignComboButton /> : <SaveButtonCombo />}
      </StyledButtonGroup>
    </EditorFooterWrapper>
  );
};

const DefaultFooter = ({ onClose }: TakeOverFooterProps) => (
  <DefaultFooterWrapper>
    <Button
      data-test={'btn-done'}
      onClick={() => onClose()}
      size={BUTTON_SIZES.LARGE}
      variant={BUTTON_VARIANTS.SECONDARY}
    >
      Done
    </Button>
  </DefaultFooterWrapper>
);

const InStudioTemplateEditorFooter = (props: TakeOverFooterProps) => {
  const { view } = useInStudioTakeoverContext();
  const { medium } = useTemplateQueryContext();
  const Footer =
    (medium === MessageMedium.Email || medium === MessageMedium.InApp) &&
    view === TemplateEditViews.content
      ? EditorFooter
      : DefaultFooter;
  return <Footer {...props} />;
};

const getAppContext = (source?: ConsumingApp) =>
  ({
    [ConsumingApp.CAMPAIGN_EDITOR]: CampaignTemplateProvider,
    [ConsumingApp.JOURNEY_STUDIO]: InStudioTemplateContext,
    [ConsumingApp.EXPERIMENT_TAKEOVER]: InStudioTemplateContext,
  })[source as ConsumingApp] || InStudioTemplateContext;

// I wonder if we should just push through the medium from the studio since we already know what it is and that way we don't have
// to use the enum type of MessageMedium since it has two extra members Selected, Unselected.  These feel more like the 'state' or the medium
// this way it would protect us from having to worry about having a undefined value and we can type these better.

type InStudioTemplateEditorProps = {
  consumingApp?: ConsumingApp;
  suggestedContent?: UpdateMediumFieldsType;
};

export const InStudioTemplateEditor = ({
  consumingApp,
  suggestedContent,
}: InStudioTemplateEditorProps) => {
  const isNBAJourneysEnabled = useSelector(selectNBAJourneysEnabled);
  const { setView } = useQueryParamContext();
  const { view } = useInStudioTakeoverContext();
  const { medium } = useTemplateQueryContext();
  const { onFetchLists } = useSendTestMsgContext();
  // probably rename the LoadedView component to something better.
  const { isLoading, loadedView: LoadedView } = useEditorViewLoader(InStudioView.Takeover);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const { revalidate } = useErrorValidationContext();
  const { isEditingSavedRowDesign } = useEditSavedRowDesignContext();

  useEffect(() => {
    onFetchLists();
    return () => setView(TemplateEditViews.settings);
  }, []);

  useEffect(() => {
    if (!hasLoadedOnce && !isLoading) {
      setHasLoadedOnce(true);
      revalidate();
    }
  }, [hasLoadedOnce, isLoading]);

  // once the BEE editor has loaded, don't unmount it
  if (isLoading && !hasLoadedOnce) return <Spinner />;

  return (
    <>
      {isEditingSavedRowDesign ? <EditSavedRowDesignWarning /> : <InStudioEditToolbar />}
      <Wrapper>
        {view === TemplateEditViews.settings && <TemplateDetailsSubHeader />}
        {isNBAJourneysEnabled && suggestedContent && (
          <NBASuggestion
            suggestedContent={suggestedContent}
            isSettingsView={view === TemplateEditViews.settings}
          />
        )}
        {medium === UnselectedMessageMedium.Unselected ? <Spinner /> : <LoadedView />}
      </Wrapper>
      <InStudioUnsavedChangesPrompts consumingApp={consumingApp} />
      <CommonModals />
      <SendTestMsgModals />
    </>
  );
};

interface EditTemplateTakeoverProps extends InStudioTemplateProps {
  /** string representation of which type of templates to display */
  messageMedium: AllMessageMedium;
  /** Callback when the takeover is closed */
  onClose: () => void;
  /** Attach selected template */
  onAttachTemplate?: (
    template: { id: number; locale: string },
    dataFeedId: number | null,
  ) => void | (() => NoOp);
  /** Previously selected template ID */
  attachedTemplateId?: number;
  /** Current campaign ID from node data */
  campaignId?: number;
  /** The title of the takeover */
  title?: string;
  /** Which App is consuming this takeover editor */
  consumingApp?: ConsumingApp;
  /** The placement id -- currently only used for embedded messaging */
  placementId?: number;
  /** The campaign's campaignType definition */
  campaignType?: CampaignType;
  /** NBA Suggested template content */
  suggestedContent?: UpdateMediumFieldsType;
}

const EditTemplateTakeover: FC<React.PropsWithChildren<EditTemplateTakeoverProps>> = ({
  onClose,
  messageMedium,
  attachedTemplateId,
  templateId,
  consumingApp,
  title,
  suggestedContent,
}: EditTemplateTakeoverProps): JSX.Element | null => {
  const { view } = useInStudioTakeoverContext();

  const EditorContext = useEditorContext({ messageMedium });
  const { shouldCloseTakeover, setCheckForUnsavedChanges } = useInStudioTakeoverContext();
  const { campaign } = useCampaignQueryContext();
  const { campaignState } = campaign;
  const { refetch: refetchLocales } = useLocalesQueryContext();
  const { refetch: refetchTemplateEditorTemplate, templateType } = useTemplateQueryContext();
  const { setIsEditMode } = useEditableFieldsContext();
  const { isEditingSavedRowDesign, savedRow } = useEditSavedRowDesignContext();

  const [shouldRefetch] = useState(attachedTemplateId !== templateId);
  const isSupportChatTestEnabled = useSelector(selectIsSupportChatTestFeatureFlagOn);

  const onRequestCloseSavedRowDesignEditor = useOnRequestCloseSavedRowDesign();

  useEffect(() => {
    refetchLocales();
    refetchTemplateEditorTemplate();
    if (
      (view === TemplateEditViews.settings || view === TemplateEditViews.content) &&
      // For WhatsApp Business Initiated template we should not open in edit mode since BI templates have approval status and should not be editable for most cases
      templateType !== TemplateType.BusinessInitiated
    ) {
      setIsEditMode(true);
    }
  }, []);

  useEffect(() => {
    if (EditorContext && shouldRefetch) refetchTemplateEditorTemplate();
  }, [EditorContext]);

  useEffect(() => {
    if (shouldCloseTakeover) onClose();
  }, [shouldCloseTakeover]);

  return (
    <EditorContext>
      <Takeover
        isOverlay={consumingApp === ConsumingApp.CAMPAIGN_EDITOR}
        onClose={() => {
          if (isEditingSavedRowDesign) {
            onRequestCloseSavedRowDesignEditor();
          } else {
            setCheckForUnsavedChanges(true);
          }
        }}
        data-test="edit-template-takeover"
        title={savedRow?.name || title || campaign?.name || ' '}
        noPadding
        isMainApp={consumingApp === ConsumingApp.CAMPAIGN_EDITOR}
        HeaderBarContent={isSupportChatTestEnabled && <ChatButton />}
        CloseIcon={isEditingSavedRowDesign ? Exit24 : ArrowBack}
        Badge={
          isEditingSavedRowDesign ? null : (
            <Badge variant={BADGE_VARIANTS_FOR_CAMPAIGN_STATUSES[campaignState]}>
              {campaignState}
            </Badge>
          )
        }
        Body={
          <InStudioTemplateEditor consumingApp={consumingApp} suggestedContent={suggestedContent} />
        }
        Footer={<InStudioTemplateEditorFooter onClose={() => setCheckForUnsavedChanges(true)} />}
      />
    </EditorContext>
  );
};

/*
This editor is used by two different parts of the app and requires different contexts for each one of them.  The consuming app is passed in as a prop and the context is determined based on that prop.
The campaign editor itself is wrapped by many of these contexts at the entry point of the application, so we have a smaller context tree to deal with here.  The journey editor is not wrapped by these contexts and so we have to wrap it here.
*/
export default (props: EditTemplateTakeoverProps) => {
  const {
    onClose,
    messageMedium,
    attachedTemplateId,
    onAttachTemplate = () => noop,
    templateId,
    consumingApp,
    campaignId,
    placementId,
    campaignType = CampaignType.Blast,
  } = props;
  const { view } = useInStudioTakeoverContext();
  const showTemplateSelector =
    view === TemplateEditViews.select || templateId === null || templateId === 0;

  const AppContext = getAppContext(consumingApp);

  if (view) {
    return (
      <div>
        {showTemplateSelector ? (
          <AttachTemplateTakeover
            onClose={onClose}
            consumingApp={consumingApp as ConsumingApp}
            attachedTemplateId={attachedTemplateId}
            messageMedium={messageMedium}
            campaignType={campaignType}
            onAttachTemplate={onAttachTemplate}
            campaignId={campaignId}
            placementId={placementId}
          />
        ) : (
          <AppContext {...props}>
            <EditTemplateTakeover {...props} />
          </AppContext>
        )}
      </div>
    );
  }
  return null;
};
